import { IVideoToCache, TimeSpan } from 'fitbeat.models';
import { getDefaultExerciseVersion } from '../config';
import { IGymStateSessionViewModel, SessionSequence } from '../entities';

export interface ISignedCdnPathOptions {
  cdnHostPath: string;
  signingKeyName: string;
  signingKeyValue: string;
}

export interface IVideoMediaCdn extends ISignedCdnPathOptions {
  videoFolder: string;
}

interface ICrypto {
  createHmac: (algo: string, value: Buffer) => {
    update: (value: string) => {
      digest: () => Buffer,
    },
  };
}

interface IUrlSafeBase64 {
  encode: (value: Buffer) => string;
  decode: (value: string) => Buffer;
}

export class CdnHelpers {
  constructor(private crypto: ICrypto, private URLSafeBase64: IUrlSafeBase64) { }

  public async getSignedUrlForAllMedia(signedMediaPathOptions: ISignedCdnPathOptions): Promise<{
    baseUrl: string,
    queryParamatersToAppend: string,
  }> {
    const baseUrl = new URL(signedMediaPathOptions.cdnHostPath);
    const url = new URL(
      baseUrl.pathname,
      baseUrl.origin,
    );

    const expiresOnHourFromNow = TimeSpan.oneHourFrom(new Date());
    const cdnRequiredQueryParamaters = {
      URLPrefix: this.URLSafeBase64.encode(Buffer.from(url.href)),
      Expires: expiresOnHourFromNow.getTime().toString(),
      KeyName: signedMediaPathOptions.signingKeyName,
    };
    const queryToSign = new URLSearchParams(cdnRequiredQueryParamaters);

    const decodedKey = this.URLSafeBase64.decode(signedMediaPathOptions.signingKeyValue);
    const hmac = this.crypto.createHmac('sha1', decodedKey);
    const signature = hmac.update(queryToSign.toString()).digest();
    const encodedSignature = this.URLSafeBase64.encode(signature);

    const finalQueryParamaters = new URLSearchParams(cdnRequiredQueryParamaters);
    finalQueryParamaters.append('Signature', encodedSignature);

    return {
      baseUrl: url.href,
      queryParamatersToAppend: finalQueryParamaters.toString(),
    };
  }

  public async getSignedUrlForMedia(
    expiryDate: Date,
    fileName: string,
    signedMediaPathOptions: ISignedCdnPathOptions,
    version?: string | any,
  ): Promise<string> {
    const baseUrl = new URL(signedMediaPathOptions.cdnHostPath);
    const url = new URL(
      `${baseUrl.pathname}${fileName}`,
      baseUrl.origin,
    );

    const cdnRequiredQueryParamaters = {
      URLPrefix: this.URLSafeBase64.encode(Buffer.from(url.href)),
      Expires: expiryDate.getTime().toString(),
      KeyName: signedMediaPathOptions.signingKeyName,
    };
    const queryToSign = new URLSearchParams(cdnRequiredQueryParamaters);

    const decodedKey = this.URLSafeBase64.decode(signedMediaPathOptions.signingKeyValue);
    const hmac = this.crypto.createHmac('sha1', decodedKey);
    const signature = hmac.update(queryToSign.toString()).digest();
    const encodedSignature = this.URLSafeBase64.encode(signature);

    const finalQueryParamaters = new URLSearchParams({
      ...version ? {
        version,
      } : {},
      ...cdnRequiredQueryParamaters,
    });

    finalQueryParamaters.append('Signature', encodedSignature);
    url.search = finalQueryParamaters.toString();
    return url.href;
  }

  public async mapSequencesToCdnUrls(
    session: IGymStateSessionViewModel,
    isHtmlVideo: boolean,
    signedMediaPathOptions: IVideoMediaCdn,
  ) {
    if (!isHtmlVideo) {
      return session;
    }

    const sessionSequence = session.sessionSequence as SessionSequence[];
    for (const sequenceEntry of sessionSequence) {
      for (const sequence of sequenceEntry.sequence) {
        if (!sequence.videoFile) {
          continue;
        }

        sequence.videoFile = await this.mapReferenceToSignedCdnUrl({
          reference: sequence.videoFile || '',
          version: sequence.exerciseVersion,
        }, signedMediaPathOptions);
      }
    }
    return session;
  }

  public async mapVideosToCacheToSignedUrls(
    videosToCache: IVideoToCache[],
    signedMediaPathOptions: IVideoMediaCdn,
  ) {
    const allSignedUrls = [];
    for (const videoToCache of videosToCache) {
      const signedUrl = await this.mapReferenceToSignedCdnUrl(videoToCache, signedMediaPathOptions);
      allSignedUrls.push(signedUrl);
    }
    return allSignedUrls;
  }

  public async mapReferenceToSignedCdnUrl(
    videoToCache: IVideoToCache,
    signedMediaPathOptions: IVideoMediaCdn,
  ): Promise<string> {
    // TODO: make this time dynamic based on type
    const expiresAt = new Date(TimeSpan.getNextMonday(new Date()));
    const signedUrl = await this.getSignedUrlForMedia(
      expiresAt,
      `${signedMediaPathOptions.videoFolder}/${videoToCache.reference.toString().trim()}.mp4`,
      signedMediaPathOptions,
      videoToCache.version || getDefaultExerciseVersion(),
    );
    return signedUrl;
  }
}
